package com.kanati.admin.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextAreaField
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.formatTaxRateInput
import com.kanati.admin.state.formatTaxRatePercent
import com.kanati.admin.state.parseTaxRateInputToBps
import com.kanati.shared.consumer.ConsumerAppRuntimeSettings
import com.kanati.shared.district.DistrictSettingsResponse
import com.kanati.shared.district.UpdateDistrictSettingsRequest
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private val runtimeJson = Json {
    prettyPrint = true
    explicitNulls = false
}

private val defaultConsumerTabRouteIds = listOf("home", "events", "order", "account")
private const val defaultConsumerHomeTabId = "home"

private data class ThemeFieldSpec(
    val key: String,
    val label: String,
    val placeholder: String,
)

private data class ConsumerRuntimePreviewModel(
    val defaultScreenId: String?,
    val fallbackEyebrow: String,
    val fallbackTitle: String,
    val fallbackBody: String,
    val fallbackButtons: List<String>,
    val screens: List<ConsumerRuntimeScreenPreview>,
)

private data class ConsumerRuntimeScreenPreview(
    val id: String,
    val label: String,
    val blockCount: Int,
    val isRoot: Boolean,
    val previewEyebrow: String,
    val previewTitle: String,
    val previewBody: String,
    val previewButtons: List<String>,
)

private enum class AppDocumentValidationTone {
    Success,
    Info,
    Error,
}

private data class AppDocumentValidation(
    val tone: AppDocumentValidationTone,
    val message: String,
)

private val commonThemeFields = listOf(
    ThemeFieldSpec("primary", "Primary", "#0B5FFF"),
    ThemeFieldSpec("onPrimary", "On Primary", "#FFFFFF"),
    ThemeFieldSpec("secondary", "Secondary", "#3BB273"),
    ThemeFieldSpec("onSecondary", "On Secondary", "#0C1B14"),
    ThemeFieldSpec("background", "Background", "#F5F7FB"),
    ThemeFieldSpec("onBackground", "On Background", "#102033"),
    ThemeFieldSpec("surface", "Surface", "#FFFFFF"),
    ThemeFieldSpec("onSurface", "On Surface", "#122033"),
    ThemeFieldSpec("surfaceVariant", "Surface Variant", "#DCE7F5"),
    ThemeFieldSpec("onSurfaceVariant", "On Surface Variant", "#50627A"),
    ThemeFieldSpec("outline", "Outline", "#9EB0C6"),
    ThemeFieldSpec("logoUrl", "Logo URL", "https://example.com/logo.png"),
)

@Composable
fun SettingsScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
    onDistrictTaxRateUpdated: (Int) -> Unit,
    canEdit: Boolean,
) {
    val scope = rememberCoroutineScope()
    val selectedDistrict = district
    var settings by remember { mutableStateOf<DistrictSettingsResponse?>(null) }
    var draftTaxRate by remember { mutableStateOf("") }
    var draftConsumerThemeJson by remember { mutableStateOf("") }
    var draftAppDocumentJson by remember { mutableStateOf("") }
    var draftRequiredTabRouteIds by remember { mutableStateOf(defaultConsumerTabRouteIds.joinToString(", ")) }
    var draftHomeTabId by remember { mutableStateOf(defaultConsumerHomeTabId) }
    var showAdvancedAppDocumentJson by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }
    var isSaving by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var taxSaveError by remember { mutableStateOf<String?>(null) }
    var taxSaveMessage by remember { mutableStateOf<String?>(null) }
    var runtimeSaveError by remember { mutableStateOf<String?>(null) }
    var runtimeSaveMessage by remember { mutableStateOf<String?>(null) }
    var runtimeSaveToastVisible by remember { mutableStateOf(false) }
    val parsedThemeObject = remember(draftConsumerThemeJson) { parseJsonObjectOrNull(draftConsumerThemeJson) }
    val themeJsonError = remember(draftConsumerThemeJson) { jsonObjectError(draftConsumerThemeJson) }
    val appDocumentError = remember(draftAppDocumentJson) { jsonObjectError(draftAppDocumentJson) }
    val requiredTabRouteIds = remember(draftRequiredTabRouteIds) { parseRouteIds(draftRequiredTabRouteIds) }
    val normalizedHomeTabId = remember(draftHomeTabId) { draftHomeTabId.trim() }
    val previewModel = remember(draftAppDocumentJson) { buildConsumerRuntimePreviewModel(draftAppDocumentJson) }
    val appDocumentEditorAvailability = remember(
        draftAppDocumentJson,
        requiredTabRouteIds,
        normalizedHomeTabId,
    ) {
        resolveAppDocumentEditorAvailability(
            rawJson = draftAppDocumentJson,
            requiredTabRouteIds = requiredTabRouteIds,
            homeTabId = normalizedHomeTabId,
        )
    }
    var selectedPreviewScreenId by remember { mutableStateOf<String?>(null) }
    val appDocumentValidation = remember(
        draftAppDocumentJson,
        appDocumentError,
        requiredTabRouteIds,
        normalizedHomeTabId,
    ) {
        validateAppDocument(
            rawDocumentJson = draftAppDocumentJson,
            documentJsonError = appDocumentError,
            requiredTabRouteIds = requiredTabRouteIds,
            homeTabId = normalizedHomeTabId,
        )
    }

    LaunchedEffect(previewModel.screens) {
        selectedPreviewScreenId = when {
            previewModel.screens.isEmpty() -> null
            selectedPreviewScreenId != null && previewModel.screens.any { it.id == selectedPreviewScreenId } -> selectedPreviewScreenId
            else -> previewModel.defaultScreenId ?: previewModel.screens.first().id
        }
    }

    LaunchedEffect(runtimeSaveMessage) {
        if (runtimeSaveMessage == null) {
            runtimeSaveToastVisible = false
            return@LaunchedEffect
        }

        runtimeSaveToastVisible = true
        delay(2_800)
        runtimeSaveToastVisible = false
        delay(320)
        runtimeSaveMessage = null
    }

    fun settingsUnavailableMessage(): String =
        when {
            districtLoading -> "Loading district context..."
            districtLoadError != null -> "District context could not be loaded. Refresh context and select a district to manage settings."
            else -> "Select a district to manage district settings."
        }

    suspend fun loadSettings(currentDistrict: SelectedDistrictContext) {
        isLoading = true
        loadError = null
        settings = null
        try {
            val response = apiClient.getDistrictSettings(currentDistrict.id)
            settings = response
            draftTaxRate = formatTaxRateInput(response.defaultTaxRateBps)
            draftConsumerThemeJson = response.consumerApp?.nativeUiThemeJson.orEmpty()
            draftAppDocumentJson = response.consumerApp?.appDocumentJson.orEmpty()
        } catch (exception: Exception) {
            loadError = exception.message ?: "Unable to load district settings."
        } finally {
            isLoading = false
        }
    }

    LaunchedEffect(selectedDistrict?.id, districtLoading, districtLoadError) {
        taxSaveError = null
        taxSaveMessage = null
        runtimeSaveError = null
        runtimeSaveMessage = null

        if (selectedDistrict == null) {
            settings = null
            draftTaxRate = ""
            draftConsumerThemeJson = ""
            draftAppDocumentJson = ""
            showAdvancedAppDocumentJson = false
            loadError = settingsUnavailableMessage()
            return@LaunchedEffect
        }

        loadSettings(selectedDistrict)
    }

    SectionCard(
        title = "District Settings",
        theme = theme,
        description = "Review district-wide defaults for the currently selected district.",
    ) {
        if (selectedDistrict == null) {
            StatusBanner(
                message = settingsUnavailableMessage(),
                theme = theme,
                tone = if (districtLoadError != null) BannerTone.Error else BannerTone.Info,
            )
        } else {
            if (!canEdit) {
                StatusBanner(
                    message = "Read-only access. District setting changes require district edit permission.",
                    theme = theme,
                )
            }
            SettingsSummaryPanel(
                district = selectedDistrict,
                effectiveTaxRateBps = settings?.defaultTaxRateBps ?: selectedDistrict.defaultTaxRateBps ?: 0,
                consumerRuntimeStatus = when {
                    isLoading -> "Loading..."
                    loadError != null || settings == null -> "Unknown · refresh settings"
                    settings?.consumerApp?.nativeUiThemeJson.isNullOrBlank() && settings?.consumerApp?.appDocumentJson.isNullOrBlank() -> "Bundled defaults allowed"
                    else -> "District overrides stored"
                },
                theme = theme,
            )
        }
    }

    if (selectedDistrict != null) {
        SectionCard(
            title = "Sales Tax",
            theme = theme,
            description = "Taxable products use this district default sales tax rate. Tax-exempt products always use zero tax.",
            headerActions = {
                ActionButton(
                    label = if (isLoading) "Loading..." else "Refresh",
                    onClick = {
                        scope.launch {
                            loadSettings(selectedDistrict)
                        }
                    },
                    theme = theme,
                    isPrimary = false,
                    isDisabled = isLoading || isSaving,
                )
            },
        ) {
            loadError?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Error,
                )
            }

            taxSaveError?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Error,
                )
            }

            taxSaveMessage?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Success,
                )
            }

            settings?.taxRules?.takeIf { it.isNotEmpty() }?.let { taxRules ->
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                            gap(10.px)
                            padding(16.px)
                            borderRadius(22.px)
                            property(
                                "background",
                                "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                            )
                            property("border", "1px solid ${theme.outlineVariant}")
                            property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                        }
                    }
                ) {
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "0.8rem")
                                property("font-weight", "700")
                                property("letter-spacing", "0.08em")
                                property("text-transform", "uppercase")
                                property("color", theme.onSurfaceVariant)
                            }
                        }
                    ) {
                        Text("Returned Tax Rules")
                    }

                    taxRules.forEach { taxRule ->
                        P(
                            attrs = {
                                style {
                                    margin(0.px)
                                    property("color", theme.onSurfaceVariant)
                                    property("font-size", "0.92rem")
                                    property("line-height", "1.5")
                                }
                            }
                        ) {
                            Text("${taxRule.name}: ${formatTaxRatePercent(taxRule.rateBps)}")
                        }
                    }
                }
            }

            TextField(
                theme = theme,
                label = "Default Sales Tax Rate",
                value = draftTaxRate,
                placeholder = "7.25",
                onValueChange = {
                    if (canEdit) {
                        draftTaxRate = it
                        taxSaveError = null
                        taxSaveMessage = null
                    }
                },
            )

            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("color", theme.onSurfaceVariant)
                        property("font-size", "0.9rem")
                        property("line-height", "1.5")
                    }
                }
            ) {
                Text("Enter a percentage such as 7.25. Kanati stores this as basis points and applies it only to products marked Taxable.")
            }

            ActionButton(
                label = if (isSaving) "Saving..." else "Save Sales Tax",
                onClick = {
                    if (!canEdit) return@ActionButton
                    val basisPoints = parseTaxRateInputToBps(draftTaxRate)
                    when {
                        basisPoints == null -> taxSaveError = "Enter a valid sales tax rate such as 7.25."
                        basisPoints !in 0..10_000 -> taxSaveError = "Enter a sales tax rate between 0 and 100."
                        else -> scope.launch {
                            isSaving = true
                            taxSaveError = null
                            taxSaveMessage = null
                            try {
                                val updated = apiClient.updateDistrictSettings(
                                    districtId = selectedDistrict.id,
                                    request = UpdateDistrictSettingsRequest(
                                        defaultTaxRateBps = basisPoints,
                                        consumerApp = settings?.consumerApp,
                                    )
                                )
                                settings = updated
                                draftTaxRate = formatTaxRateInput(updated.defaultTaxRateBps)
                                onDistrictTaxRateUpdated(updated.defaultTaxRateBps)
                                taxSaveMessage = "Saved. Taxable products in ${selectedDistrict.name} now use ${formatTaxRatePercent(updated.defaultTaxRateBps)}."
                            } catch (exception: Exception) {
                                taxSaveError = exception.message ?: "Unable to save district settings."
                            } finally {
                                isSaving = false
                            }
                        }
                    }
                },
                theme = theme,
                isDisabled = isLoading || isSaving || !canEdit || settings == null || loadError != null,
            )
        }

        SectionCard(
            title = "Consumer Runtime",
            theme = theme,
            description = "District-owned Consumer theme and page settings. Saving makes configuration available to school lookups; verify rendering in the installed Android app before launch. The preview does not certify hosted-content or device compatibility.",
            headerActions = {
                ActionButton(
                    label = if (isLoading) "Loading..." else "Refresh",
                    onClick = {
                        scope.launch {
                            loadSettings(selectedDistrict)
                        }
                    },
                    theme = theme,
                    isPrimary = false,
                    isDisabled = isLoading || isSaving,
                )
            },
        ) {
            loadError?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Error,
                )
            }

            runtimeSaveError?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Error,
                )
            }

            runtimeSaveMessage?.let {
                ConsumerRuntimeSaveToast(
                    message = it,
                    theme = theme,
                    isVisible = runtimeSaveToastVisible,
                )
            }

            themeJsonError?.let {
                StatusBanner(
                    message = "Theme preview is unavailable right now: $it",
                    theme = theme,
                    tone = BannerTone.Info,
                )
            }

            appDocumentError?.let {
                StatusBanner(
                    message = "Page preview is unavailable right now: $it",
                    theme = theme,
                    tone = BannerTone.Info,
                )
            }

            appDocumentValidation?.let { validation ->
                StatusBanner(
                    message = validation.message,
                    theme = theme,
                    tone = when (validation.tone) {
                        AppDocumentValidationTone.Success -> BannerTone.Success
                        AppDocumentValidationTone.Info -> BannerTone.Info
                        AppDocumentValidationTone.Error -> BannerTone.Error
                    },
                )
            }

            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                        gap(18.px)
                    }
                }
            ) {
                P(
                    attrs = {
                        style {
                            margin(0.px)
                            property("color", theme.onSurfaceVariant)
                            property("font-size", "0.9rem")
                            property("line-height", "1.55")
                        }
                    }
                ) {
                    Text("Use these fields for the most common colors and branding details. Page editing happens below, and advanced JSON is still available when you need full control.")
                }

                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Tab Page IDs",
                        value = draftRequiredTabRouteIds,
                        placeholder = "home, events, wallet, account",
                        onValueChange = {
                            if (canEdit) {
                                draftRequiredTabRouteIds = it
                                runtimeSaveError = null
                                runtimeSaveMessage = null
                            }
                        },
                    )
                    TextField(
                        theme = theme,
                        label = "Home Tab ID",
                        value = draftHomeTabId,
                        placeholder = "home",
                        onValueChange = {
                            if (canEdit) {
                                draftHomeTabId = it
                                runtimeSaveError = null
                                runtimeSaveMessage = null
                            }
                        },
                    )
                }

                P(
                    attrs = {
                        style {
                            margin(0.px)
                            property("color", theme.onSurfaceVariant)
                            property("font-size", "0.9rem")
                            property("line-height", "1.55")
                        }
                    }
                ) {
                    Text("These IDs match the app tabs. This page setup can fill those tabs with custom pages, but the tab order, labels, and icons still come from the app build.")
                }

                commonThemeFields.chunked(2).forEach { fieldRow ->
                    FieldRow {
                        fieldRow.forEach { field ->
                            ThemeDraftField(
                                field = field,
                                currentValue = parsedThemeObject.themeValue(field.key),
                                theme = theme,
                                onValueChange = { nextValue ->
                                    if (canEdit) {
                                        draftConsumerThemeJson = updateThemeJsonField(
                                            currentJson = draftConsumerThemeJson,
                                            key = field.key,
                                            value = nextValue,
                                        )
                                        runtimeSaveError = null
                                        runtimeSaveMessage = null
                                    }
                                },
                            )
                        }
                    }
                }

                ThemePreviewPaletteCard(
                    theme = theme,
                    previewTheme = resolvedPreviewTheme(
                        adminTheme = theme,
                        themeObject = parsedThemeObject,
                    ),
                )
            }

            TextAreaField(
                theme = theme,
                label = "Advanced Theme JSON",
                value = draftConsumerThemeJson,
                placeholder = """{"primary":"#0B5FFF","onPrimary":"#FFFFFF"}""",
                rows = 8,
                indentWithSpaces = 2,
                labelAction = {
                    EditorUtilityButton(
                        label = "Pretty JSON",
                        theme = theme,
                        isDisabled = draftConsumerThemeJson.trim().isBlank() || themeJsonError != null || !canEdit,
                        onClick = {
                            if (!canEdit) return@EditorUtilityButton
                            draftConsumerThemeJson = prettyPrintJson(draftConsumerThemeJson)
                            runtimeSaveError = null
                            runtimeSaveMessage = null
                        },
                    )
                },
                onValueChange = {
                    if (canEdit) {
                        draftConsumerThemeJson = it
                        runtimeSaveError = null
                        runtimeSaveMessage = null
                    }
                },
            )

            when (val availability = appDocumentEditorAvailability) {
                is AppDocumentEditorAvailability.Blank -> {
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                                gap(14.px)
                                padding(18.px)
                                borderRadius(22.px)
                                property("background", theme.surfaceContainerHighest)
                                property("border", "1px solid ${theme.outlineVariant}")
                                property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                            }
                        }
                    ) {
                        P(
                            attrs = {
                                style {
                                    margin(0.px)
                                    property("font-size", "0.88rem")
                                    property("font-weight", "700")
                                    property("letter-spacing", "0.08em")
                                    property("text-transform", "uppercase")
                                    property("color", theme.onSurfaceVariant)
                                }
                            }
                        ) {
                            Text("Page Setup")
                        }
                        P(
                            attrs = {
                                style {
                                    margin(0.px)
                                    property("font-size", "0.96rem")
                                    property("line-height", "1.6")
                                    property("color", theme.onSurfaceVariant)
                                }
                            }
                        ) {
                            Text("No page setup has been started yet. Create a guided draft from your tabs, or open the advanced JSON editor if you want to paste the full page setup yourself.")
                        }
                        FieldRow {
                            ActionButton(
                                label = "Create Guided Page Setup",
                                onClick = {
                                    if (!canEdit) return@ActionButton
                                    draftAppDocumentJson = encodeDocumentDraft(availability.suggestion)
                                    selectedPreviewScreenId = availability.suggestion.rootPageId
                                    runtimeSaveError = null
                                    runtimeSaveMessage = null
                                },
                                theme = theme,
                                isDisabled = !canEdit,
                            )
                            ActionButton(
                                label = if (showAdvancedAppDocumentJson) "Hide Advanced JSON" else "Show Advanced JSON",
                                onClick = { showAdvancedAppDocumentJson = !showAdvancedAppDocumentJson },
                                theme = theme,
                                isDisabled = false,
                                isPrimary = false,
                            )
                        }
                    }
                }

                is AppDocumentEditorAvailability.RawFallback -> {
                    StatusBanner(
                        message = availability.message,
                        theme = theme,
                        tone = BannerTone.Info,
                    )
                }

                is AppDocumentEditorAvailability.Ready -> {
                    FieldRow {
                        ActionButton(
                            label = if (showAdvancedAppDocumentJson) "Hide Advanced JSON" else "Show Advanced JSON",
                            onClick = { showAdvancedAppDocumentJson = !showAdvancedAppDocumentJson },
                            theme = theme,
                            isDisabled = false,
                            isPrimary = false,
                        )
                    }
                    NativeUiDocumentGuidedEditor(
                        draft = availability.draft,
                        selectedPageId = selectedPreviewScreenId,
                        onSelectedPageChange = { selectedPreviewScreenId = it },
                        requiredTabRouteIds = requiredTabRouteIds,
                        homeTabId = normalizedHomeTabId,
                        canEdit = canEdit,
                        theme = theme,
                        onChange = { nextDraft ->
                            if (canEdit) {
                                draftAppDocumentJson = encodeDocumentDraft(nextDraft)
                                runtimeSaveError = null
                                runtimeSaveMessage = null
                            }
                        },
                    )
                }
            }

            if (
                showAdvancedAppDocumentJson ||
                appDocumentEditorAvailability is AppDocumentEditorAvailability.RawFallback ||
                appDocumentError != null
            ) {
                TextAreaField(
                    theme = theme,
                    label = "Advanced App Document JSON",
                    value = draftAppDocumentJson,
                    placeholder = """{"id":"selu-app","version":"1.0","rootPageId":"home","pages":[{"id":"home","title":"Home","blocks":[]},{"id":"events","title":"Events","blocks":[]}]}""",
                    rows = 14,
                    indentWithSpaces = 2,
                    labelAction = {
                        EditorUtilityButton(
                            label = "Pretty JSON",
                            theme = theme,
                            isDisabled = draftAppDocumentJson.trim().isBlank() || appDocumentError != null || !canEdit,
                            onClick = {
                                if (!canEdit) return@EditorUtilityButton
                                draftAppDocumentJson = prettyPrintJson(draftAppDocumentJson)
                                runtimeSaveError = null
                                runtimeSaveMessage = null
                            },
                        )
                    },
                    onValueChange = {
                        if (canEdit) {
                            draftAppDocumentJson = it
                            runtimeSaveError = null
                            runtimeSaveMessage = null
                        }
                    },
                )
            }

            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("color", theme.onSurfaceVariant)
                        property("font-size", "0.9rem")
                        property("line-height", "1.55")
                    }
                }
            ) {
                Text("Leave either field blank if you want the app to keep using its built-in defaults. Save a page setup here only when you want to override the app's pages for this district.")
            }

            ConsumerRuntimePreviewCard(
                theme = theme,
                previewTheme = resolvedPreviewTheme(
                    adminTheme = theme,
                    themeObject = parsedThemeObject,
                ),
                preview = previewModel,
                selectedScreenId = selectedPreviewScreenId,
                onScreenSelected = { selectedPreviewScreenId = it },
                themeJsonError = themeJsonError,
                appDocumentError = appDocumentError,
            )

            ActionButton(
                label = if (isSaving) "Saving..." else "Save Consumer App Runtime",
                onClick = {
                    if (!canEdit) return@ActionButton
                    if (appDocumentValidation?.tone == AppDocumentValidationTone.Error) {
                        runtimeSaveError = appDocumentValidation.message
                        return@ActionButton
                    }
                    scope.launch {
                        isSaving = true
                        runtimeSaveError = null
                        runtimeSaveMessage = null
                        try {
                            val updated = apiClient.updateDistrictSettings(
                                districtId = selectedDistrict.id,
                                request = UpdateDistrictSettingsRequest(
                                    defaultTaxRateBps = settings?.defaultTaxRateBps
                                        ?: selectedDistrict.defaultTaxRateBps
                                        ?: 0,
                                    consumerApp = ConsumerAppRuntimeSettings(
                                        nativeUiThemeJson = draftConsumerThemeJson.trim().ifBlank { null },
                                        appDocumentJson = draftAppDocumentJson.trim().ifBlank { null },
                                    ).takeIf {
                                        it.nativeUiThemeJson != null || it.appDocumentJson != null
                                    },
                                )
                            )
                            settings = updated
                            draftConsumerThemeJson = updated.consumerApp?.nativeUiThemeJson.orEmpty()
                            draftAppDocumentJson = updated.consumerApp?.appDocumentJson.orEmpty()
                            runtimeSaveMessage = "Saved for ${selectedDistrict.name}. Updated settings are available to future Consumer reads. Verify the installed Android app before launch."
                        } catch (exception: Exception) {
                            runtimeSaveError = exception.message ?: "Unable to save consumer runtime settings."
                        } finally {
                            isSaving = false
                        }
                    }
                },
                theme = theme,
                isDisabled = isLoading || isSaving || !canEdit || settings == null || loadError != null || themeJsonError != null || appDocumentError != null || appDocumentValidation?.tone == AppDocumentValidationTone.Error,
            )
        }
    }
}

@Composable
private fun ConsumerRuntimeSaveToast(
    message: String,
    theme: MaterialThemePalette,
    isVisible: Boolean,
) {
    Div(
        attrs = {
            attr("role", "status")
            attr("aria-live", "polite")
            style {
                padding(14.px, 16.px)
                borderRadius(8.px)
                property("position", "fixed")
                property("right", "16px")
                property("bottom", "16px")
                property("z-index", "20")
                property("max-width", "min(420px, calc(100vw - 32px))")
                property("background", "rgba(22, 163, 74, 0.96)")
                property("color", "#ffffff")
                property("border", "1px solid rgba(21, 128, 61, 0.92)")
                property("box-shadow", "0 18px 38px ${theme.shadow}")
                property("opacity", if (isVisible) "1" else "0")
                property("transform", if (isVisible) "translateY(0)" else "translateY(10px)")
                property("transition", "opacity 320ms ease, transform 320ms ease")
                property("pointer-events", "none")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.95rem")
                    property("font-weight", "700")
                    property("line-height", "1.5")
                    property("white-space", "pre-wrap")
                    property("overflow-wrap", "anywhere")
                }
            }
        ) {
            Text(message)
        }
    }
}

@Composable
private fun EditorUtilityButton(
    label: String,
    theme: MaterialThemePalette,
    isDisabled: Boolean,
    onClick: () -> Unit,
) {
    Button(
        attrs = {
            onClick { if (!isDisabled) onClick() }
            if (isDisabled) disabled()
            style {
                padding(8.px, 12.px)
                borderRadius(999.px)
                property("cursor", if (isDisabled) "default" else "pointer")
                property("background", if (isDisabled) theme.surfaceContainerHighest else theme.primaryContainer)
                property("color", if (isDisabled) theme.onSurfaceVariant else theme.onPrimaryContainer)
                property("border", "1px solid ${theme.outlineVariant}")
                property("font-size", "0.78rem")
                property("font-weight", "700")
                property("white-space", "nowrap")
                if (isDisabled) {
                    property("opacity", "0.65")
                }
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun ThemeDraftField(
    field: ThemeFieldSpec,
    currentValue: String,
    theme: MaterialThemePalette,
    onValueChange: (String) -> Unit,
) {
    TextField(
        theme = theme,
        label = field.label,
        value = currentValue,
        placeholder = field.placeholder,
        onValueChange = onValueChange,
    )
}

@Composable
private fun ThemePreviewPaletteCard(
    theme: MaterialThemePalette,
    previewTheme: PreviewThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                gap(12.px)
                padding(18.px)
                borderRadius(22.px)
                property("background", theme.surfaceContainerHighest)
                property("border", "1px solid ${theme.outlineVariant}")
                property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.8rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text("Color Preview")
        }
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.9rem")
                    property("line-height", "1.5")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text("Use this quick check to see how your colors look before you move on to page editing.")
        }
        FieldRow {
            PreviewSwatch("Primary", previewTheme.primary, previewTheme.onPrimary, theme)
            PreviewSwatch("Surface", previewTheme.surface, previewTheme.onSurface, theme)
            PreviewSwatch("Background", previewTheme.background, previewTheme.onBackground, theme)
        }
    }
}

@Composable
private fun ConsumerRuntimePreviewCard(
    theme: MaterialThemePalette,
    previewTheme: PreviewThemePalette,
    preview: ConsumerRuntimePreviewModel,
    selectedScreenId: String?,
    onScreenSelected: (String) -> Unit,
    themeJsonError: String?,
    appDocumentError: String?,
) {
    val activeScreen = preview.screens.firstOrNull { it.id == selectedScreenId }
        ?: preview.screens.firstOrNull { it.id == preview.defaultScreenId }
        ?: preview.screens.firstOrNull()
    val previewEyebrow = activeScreen?.previewEyebrow ?: preview.fallbackEyebrow
    val previewTitle = activeScreen?.previewTitle ?: preview.fallbackTitle
    val previewBody = activeScreen?.previewBody ?: preview.fallbackBody
    val previewButtons = activeScreen?.previewButtons ?: preview.fallbackButtons

    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                gap(16.px)
                padding(18.px)
                borderRadius(24.px)
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                )
                property("border", "1px solid ${theme.outlineVariant}")
                property("box-shadow", "0 16px 32px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.8rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text("App Preview")
        }

        if (preview.screens.isNotEmpty()) {
            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                        gap(10.px)
                    }
                }
            ) {
                P(
                    attrs = {
                        style {
                            margin(0.px)
                            property("font-size", "0.78rem")
                            property("font-weight", "700")
                            property("letter-spacing", "0.08em")
                            property("text-transform", "uppercase")
                            property("color", theme.onSurfaceVariant)
                        }
                    }
                ) {
                    Text("Pages")
                }

                FieldRow {
                    preview.screens.forEach { screen ->
                        PreviewScreenCard(
                            screen = screen,
                            isSelected = screen.id == activeScreen?.id,
                            onClick = { onScreenSelected(screen.id) },
                            previewTheme = previewTheme,
                            theme = theme,
                        )
                    }
                }
            }
        }

        Div(
            attrs = {
                style {
                    padding(20.px)
                    borderRadius(24.px)
                    property("background", previewTheme.background)
                    property("color", previewTheme.onBackground)
                    property("border", "1px solid ${previewTheme.outline}")
                    property("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.24)")
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        gap(12.px)
                    }
                }
            ) {
                Div {
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "0.78rem")
                                property("font-weight", "700")
                                property("letter-spacing", "0.08em")
                                property("text-transform", "uppercase")
                                property("color", previewTheme.primary)
                            }
                        }
                    ) {
                        Text(previewEyebrow)
                    }
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "1.35rem")
                                property("font-weight", "700")
                                property("color", previewTheme.onBackground)
                            }
                        }
                    ) {
                        Text(previewTitle)
                    }
                }
                previewTheme.logoUrl?.let { logoUrl ->
                    Div(
                        attrs = {
                            style {
                                padding(8.px, 12.px)
                                borderRadius(999.px)
                                property("background", previewTheme.surfaceVariant)
                                property("color", previewTheme.onSurfaceVariant)
                                property("font-size", "0.78rem")
                                property("font-weight", "600")
                                property("max-width", "240px")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                        }
                    ) {
                        Text("Logo: $logoUrl")
                    }
                }
            }

            Div(
                attrs = {
                    style {
                        property("margin-top", "16px")
                        padding(18.px)
                        borderRadius(22.px)
                        property("background", previewTheme.surface)
                        property("color", previewTheme.onSurface)
                        property("border", "1px solid ${previewTheme.outline}")
                    }
                }
            ) {
                P(
                    attrs = {
                        style {
                            margin(0.px)
                            property("font-size", "1.1rem")
                            property("font-weight", "700")
                        }
                    }
                ) {
                    Text(previewTitle)
                }
                P(
                    attrs = {
                        style {
                            property("margin-top", "10px")
                            margin(0.px)
                            property("font-size", "0.96rem")
                            property("line-height", "1.6")
                            property("color", previewTheme.onSurfaceVariant)
                        }
                    }
                ) {
                    Text(previewBody)
                }
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            gap(10.px)
                            property("flex-wrap", "wrap")
                            property("margin-top", "16px")
                        }
                    }
                ) {
                    previewButtons.forEachIndexed { index, label ->
                        Div(
                            attrs = {
                                style {
                                    padding(12.px, 16.px)
                                    borderRadius(14.px)
                                    property(
                                        "background",
                                        if (index == 0) previewTheme.primary else previewTheme.surfaceVariant
                                    )
                                    property(
                                        "color",
                                        if (index == 0) previewTheme.onPrimary else previewTheme.onSurface
                                    )
                                    property(
                                        "border",
                                        if (index == 0) {
                                            "1px solid ${previewTheme.primary}"
                                        } else {
                                            "1px solid ${previewTheme.outline}"
                                        }
                                    )
                                    property("font-size", "0.92rem")
                                    property("font-weight", "700")
                                }
                            }
                        ) {
                            Text(label)
                        }
                    }
                }
            }
        }

        if (themeJsonError == null && appDocumentError == null) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.88rem")
                        property("color", theme.onSurfaceVariant)
                        property("line-height", "1.5")
                    }
                }
            ) {
                Text("This preview updates from your draft. Choose any page card to check that page before you save.")
            }
        }
    }
}

@Composable
private fun PreviewScreenCard(
    screen: ConsumerRuntimeScreenPreview,
    isSelected: Boolean,
    onClick: () -> Unit,
    previewTheme: PreviewThemePalette,
    theme: MaterialThemePalette,
) {
    Button(
        attrs = {
            onClick { onClick() }
            style {
                flex(1)
                property("min-width", "180px")
                padding(14.px)
                borderRadius(18.px)
                property("text-align", "left")
                property("cursor", "pointer")
                property("transition", "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease")
                property("transform", if (isSelected) "translateY(-1px)" else "translateY(0)")
                property("background", previewTheme.surface)
                property("color", previewTheme.onSurface)
                property("border", "1px solid ${if (isSelected) previewTheme.primary else theme.outlineVariant}")
                property(
                    "box-shadow",
                    if (isSelected) "0 10px 24px ${theme.shadow}" else "inset 0 1px 0 ${theme.scrim}"
                )
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.72rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", if (isSelected) previewTheme.primary else theme.onSurfaceVariant)
                }
            }
        ) {
            Text(if (screen.isRoot) "Root Screen" else "Screen")
        }
        P(
            attrs = {
                style {
                    property("margin-top", "8px")
                    margin(0.px)
                    property("font-size", "1rem")
                    property("font-weight", "700")
                }
            }
        ) {
            Text(screen.label)
        }
        P(
            attrs = {
                style {
                    property("margin-top", "6px")
                    margin(0.px)
                    property("font-size", "0.84rem")
                    property("color", theme.onSurfaceVariant)
                    property("line-height", "1.5")
                }
            }
        ) {
            Text("id: ${screen.id} • ${screen.blockCount} blocks")
        }
    }
}

@Composable
private fun PreviewSwatch(
    label: String,
    background: String,
    foreground: String,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                flex(1)
                property("min-width", "140px")
                padding(14.px)
                borderRadius(18.px)
                property("background", background)
                property("color", foreground)
                property("border", "1px solid ${theme.outlineVariant}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.74rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                }
            }
        ) {
            Text(label)
        }
        P(
            attrs = {
                style {
                    property("margin-top", "8px")
                    margin(0.px)
                    property("font-size", "0.86rem")
                    property("font-family", "'SFMono-Regular', 'JetBrains Mono', monospace")
                }
            }
        ) {
            Text(background)
        }
    }
}

@Composable
private fun SettingsSummaryPanel(
    district: SelectedDistrictContext,
    effectiveTaxRateBps: Int,
    consumerRuntimeStatus: String,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                gap(14.px)
                property("flex-wrap", "wrap")
            }
        }
    ) {
        SettingsFact(
            label = "District",
            value = district.name,
            theme = theme,
        )
        SettingsFact(
            label = "Timezone",
            value = district.timezone ?: "Not set",
            theme = theme,
        )
        SettingsFact(
            label = "Default Sales Tax",
            value = formatTaxRatePercent(effectiveTaxRateBps),
            theme = theme,
        )
        SettingsFact(
            label = "Consumer Runtime",
            value = consumerRuntimeStatus,
            theme = theme,
        )
    }
}

@Composable
private fun SettingsFact(
    label: String,
    value: String,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                gap(6.px)
                padding(16.px)
                borderRadius(22.px)
                flex(1)
                property("min-width", "180px")
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                )
                property("border", "1px solid ${theme.outlineVariant}")
                property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.78rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text(label)
        }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "1rem")
                    property("font-weight", "650")
                    property("color", theme.onSurface)
                    property("line-height", "1.4")
                }
            }
        ) {
            Text(value)
        }
    }
}

private data class PreviewThemePalette(
    val primary: String,
    val onPrimary: String,
    val background: String,
    val onBackground: String,
    val surface: String,
    val onSurface: String,
    val surfaceVariant: String,
    val onSurfaceVariant: String,
    val outline: String,
    val logoUrl: String?,
)

private fun resolvedPreviewTheme(
    adminTheme: MaterialThemePalette,
    themeObject: JsonObject?,
): PreviewThemePalette =
    PreviewThemePalette(
        primary = themeObject.cssColor("primary") ?: adminTheme.primary,
        onPrimary = themeObject.cssColor("onPrimary") ?: adminTheme.onPrimary,
        background = themeObject.cssColor("background") ?: adminTheme.surface,
        onBackground = themeObject.cssColor("onBackground") ?: adminTheme.onSurface,
        surface = themeObject.cssColor("surface") ?: adminTheme.surfaceContainerHighest,
        onSurface = themeObject.cssColor("onSurface") ?: adminTheme.onSurface,
        surfaceVariant = themeObject.cssColor("surfaceVariant") ?: adminTheme.surfaceVariant,
        onSurfaceVariant = themeObject.cssColor("onSurfaceVariant") ?: adminTheme.onSurfaceVariant,
        outline = themeObject.cssColor("outline") ?: adminTheme.outline,
        logoUrl = themeObject.themeValue("logoUrl").ifBlank { null },
    )

private fun parseJsonObjectOrNull(rawJson: String): JsonObject? {
    val candidate = rawJson.trim().ifBlank { return null }
    return runCatching { runtimeJson.parseToJsonElement(candidate).jsonObject }.getOrNull()
}

private fun prettyPrintJson(rawJson: String): String {
    val candidate = rawJson.trim()
    if (candidate.isBlank()) return ""
    val parsed = runtimeJson.parseToJsonElement(candidate)
    return runtimeJson.encodeToString(JsonElement.serializer(), parsed)
}

private fun jsonObjectError(rawJson: String): String? {
    val candidate = rawJson.trim()
    if (candidate.isBlank()) return null
    return runCatching {
        runtimeJson.parseToJsonElement(candidate).jsonObject
        null
    }.getOrElse { "Enter a valid JSON object." }
}

private fun updateThemeJsonField(
    currentJson: String,
    key: String,
    value: String,
): String {
    val nextValues = (parseJsonObjectOrNull(currentJson)?.toMutableMap() ?: linkedMapOf()).also { values ->
        val normalized = value.trim()
        if (normalized.isBlank()) {
            values.remove(key)
        } else {
            values[key] = JsonPrimitive(normalized)
        }
    }

    if (nextValues.isEmpty()) {
        return ""
    }

    return runtimeJson.encodeToString(JsonObject(nextValues))
}

private fun JsonObject?.themeValue(key: String): String =
    this?.get(key)?.jsonPrimitive?.contentOrNull.orEmpty()

private fun JsonObject?.cssColor(key: String): String? =
    this?.themeValue(key)?.let(::nativeUiColorToCss)

private fun nativeUiColorToCss(rawValue: String): String? {
    val normalized = rawValue.trim().removePrefix("#")
    return when {
        normalized.length == 6 && normalized.matches(Regex("[0-9A-Fa-f]{6}")) -> "#$normalized"
        normalized.length == 8 && normalized.matches(Regex("[0-9A-Fa-f]{8}")) -> {
            val alpha = normalized.substring(0, 2).toIntOrNull(16) ?: return null
            val red = normalized.substring(2, 4).toIntOrNull(16) ?: return null
            val green = normalized.substring(4, 6).toIntOrNull(16) ?: return null
            val blue = normalized.substring(6, 8).toIntOrNull(16) ?: return null
            "rgba($red, $green, $blue, ${alpha.toDouble() / 255.0})"
        }
        else -> null
    }
}

private fun buildConsumerRuntimePreviewModel(rawDocumentJson: String): ConsumerRuntimePreviewModel {
    val jsonObject = parseJsonObjectOrNull(rawDocumentJson)
    val rootPageId = jsonObject?.get("rootPageId")?.jsonPrimitive?.contentOrNull
    val pages = jsonObject
        ?.get("pages")
        ?.jsonArray
        ?.mapNotNull { runCatching { it.jsonObject }.getOrNull() }
        .orEmpty()
    val pageObject = pages.firstOrNull { page ->
        page["id"]?.jsonPrimitive?.contentOrNull == rootPageId
    } ?: pages.firstOrNull()
    val selectedPageId = pageObject?.get("id")?.jsonPrimitive?.contentOrNull
    val flattenedBlocks = pageObject
        ?.get("blocks")
        ?.jsonArray
        ?.flatMap(::flattenBlocks)
        .orEmpty()

    val eyebrow = flattenedBlocks.firstText("Label")
        ?: pageObject?.get("title")?.jsonPrimitive?.contentOrNull
        ?: selectedPageId
        ?: "Runtime App"
    val title = flattenedBlocks.firstText("Title")
        ?: pageObject?.get("title")?.jsonPrimitive?.contentOrNull
        ?: if (pages.size > 1) "Runtime App Preview" else "Runtime Screen Preview"
    val body = flattenedBlocks.firstText("Body")
        ?: buildPreviewBody(pageId = selectedPageId, isRoot = selectedPageId == rootPageId, pageCount = pages.size)
    val buttons = flattenedBlocks.buttonLabels().ifEmpty {
        listOf("Primary Action", "Secondary Action")
    }

    return ConsumerRuntimePreviewModel(
        defaultScreenId = selectedPageId,
        fallbackEyebrow = eyebrow,
        fallbackTitle = title,
        fallbackBody = body,
        fallbackButtons = buttons,
        screens = pages.map { page ->
            val pageId = page["id"]?.jsonPrimitive?.contentOrNull ?: "untitled"
            val pageTitle = page["title"]?.jsonPrimitive?.contentOrNull ?: pageId
            val blockCount = page["blocks"]?.jsonArray?.size ?: 0
            val pageBlocks = page["blocks"]
                ?.jsonArray
                ?.flatMap(::flattenBlocks)
                .orEmpty()
            ConsumerRuntimeScreenPreview(
                id = pageId,
                label = pageTitle,
                blockCount = blockCount,
                isRoot = pageId == rootPageId,
                previewEyebrow = pageBlocks.firstText("Label") ?: pageTitle,
                previewTitle = pageBlocks.firstText("Title") ?: pageTitle,
                previewBody = pageBlocks.firstText("Body")
                    ?: buildPreviewBody(pageId = pageId, isRoot = pageId == rootPageId, pageCount = pages.size),
                previewButtons = pageBlocks.buttonLabels().ifEmpty {
                    listOf("Primary Action", "Secondary Action")
                },
            )
        },
    )
}

private fun buildPreviewBody(
    pageId: String?,
    isRoot: Boolean,
    pageCount: Int,
): String =
    when {
        pageCount > 1 && isRoot && pageId != null -> "This preview starts on \"$pageId\" and includes $pageCount pages."
        pageCount > 1 && pageId != null -> "This is the \"$pageId\" page from a $pageCount-page app."
        pageCount > 1 -> "This preview shows the first page from a $pageCount-page app."
        pageCount == 1 && pageId != null -> "This preview shows the \"$pageId\" page before you save."
        pageCount == 1 -> "This preview shows the current page before you save."
        else -> "Preview your app pages here before you save."
    }

private fun parseRouteIds(rawValue: String): List<String> =
    rawValue
        .split(",")
        .map { it.trim() }
        .filter { it.isNotEmpty() }
        .distinct()

private fun validateAppDocument(
    rawDocumentJson: String,
    documentJsonError: String?,
    requiredTabRouteIds: List<String>,
    homeTabId: String,
): AppDocumentValidation? {
    val candidate = rawDocumentJson.trim()
    if (candidate.isBlank() || documentJsonError != null) {
        return null
    }
    if (requiredTabRouteIds.isEmpty()) {
        return AppDocumentValidation(
            tone = AppDocumentValidationTone.Error,
            message = "Add at least one tab page ID before you save.",
        )
    }
    if (homeTabId.isBlank()) {
        return AppDocumentValidation(
            tone = AppDocumentValidationTone.Error,
            message = "Choose which tab should be the home tab before you save.",
        )
    }
    if (homeTabId !in requiredTabRouteIds) {
        return AppDocumentValidation(
            tone = AppDocumentValidationTone.Error,
            message = "The home tab must match one of your tab page IDs.",
        )
    }

    val documentObject = parseJsonObjectOrNull(candidate) ?: return null
    val rootPageId = documentObject["rootPageId"]?.jsonPrimitive?.contentOrNull?.trim().orEmpty()
    val pageIds = documentObject["pages"]
        ?.jsonArray
        ?.mapNotNull { runCatching { it.jsonObject }.getOrNull() }
        ?.mapNotNull { page -> page["id"]?.jsonPrimitive?.contentOrNull?.trim()?.ifBlank { null } }
        .orEmpty()
        .distinct()

    if (rootPageId.isBlank()) {
        return AppDocumentValidation(
            tone = AppDocumentValidationTone.Error,
            message = "Choose a starting page before you save.",
        )
    }
    if (pageIds.isEmpty()) {
        return AppDocumentValidation(
            tone = AppDocumentValidationTone.Error,
            message = "Add at least one page before you save.",
        )
    }
    if (rootPageId !in pageIds) {
        return AppDocumentValidation(
            tone = AppDocumentValidationTone.Error,
            message = "The starting page has to match one of the pages in this list.",
        )
    }

    val missingTabRouteIds = requiredTabRouteIds.filterNot(pageIds::contains)
    return if (missingTabRouteIds.isEmpty()) {
        AppDocumentValidation(
            tone = AppDocumentValidationTone.Success,
            message = "All tab pages are set up: ${requiredTabRouteIds.joinToString(", ")}.",
        )
    } else {
        AppDocumentValidation(
            tone = AppDocumentValidationTone.Info,
            message = "These tabs still use the app's built-in pages: ${missingTabRouteIds.joinToString(", ")}. You can save now and finish the rest later.",
        )
    }
}

private fun flattenBlocks(element: kotlinx.serialization.json.JsonElement): List<JsonObject> {
    val block = runCatching { element.jsonObject }.getOrNull() ?: return emptyList()
    val children = block["blocks"]
        ?.let { childElement ->
            runCatching { childElement.jsonArray.flatMap(::flattenBlocks) }.getOrElse { emptyList() }
        }
        .orEmpty()
    return listOf(block) + children
}

private fun List<JsonObject>.firstText(variant: String): String? =
    firstOrNull { block ->
        block["type"]?.jsonPrimitive?.contentOrNull == "text" &&
            block["variant"]?.jsonPrimitive?.contentOrNull == variant
    }?.get("text")?.jsonPrimitive?.contentOrNull

private fun List<JsonObject>.buttonLabels(): List<String> =
    mapNotNull { block ->
        if (block["type"]?.jsonPrimitive?.contentOrNull == "button") {
            block["label"]?.jsonPrimitive?.contentOrNull
        } else {
            null
        }
    }
